import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { PageHeader, DataTable } from "@/components/common";
import { Can } from "@/hooks/useCan/Can";
import { Button } from "@/components/ui/button";
import {
  Plus,
  Settings,
  Trash2,
  MoreHorizontal,
  ClipboardCheck,
} from "lucide-react";
import { useAuthStore } from "@/features/auth";
import { useEffect, useMemo, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import { useCarbonSurveysStore } from "../hooks";
import DeleteCarbonSurveyDialog from "../components/DeleteCarbonSurveyDialog";

const CarbonSurveysPage = () => {
  const { t } = useTran();
  const navigate = useNavigate();

  const [deleteSurveyDialog, setDeleteSurveyDialog] = useState(null);
  const [selectedSurvey, setSelectedSurvey] = useState(null);

  const onDeleteTarget = (target) => {
    setDeleteSurveyDialog(true);
    setSelectedSurvey(target);
  };

  const {
    carbonSurveysList,
    getCarbonInventorySurvey,
    page,
    pageSize,
    total,
    setPage,
    setPageSize,
  } = useCarbonSurveysStore();

  const { user } = useAuthStore();
  const permissions = useMemo(
    () => user?.permissions || [],
    [user?.permissions],
  );

  const columns = useMemo(
    () => [
      {
        key: "year",
        header: t("Year"),
        render: (row) => row.reportingYear,
      },
      {
        key: "status",
        header: t("Status"),
        render: (row) => row.status,
      },
      {
        key: "bank",
        header: t("Bank"),
        render: (row) => row.bankReview?.status,
      },
      // {
      //   key: "consent",
      //   header: t("Consent"),
      //   render: (row) => row.consent?.status,
      // },
      {
        key: "scope1",
        header: t("Scope 1 total (tCO₂e)"),
        render: (row) => row.totals?.scope1TotalTco2e || 0,
      },
      {
        key: "scope2",
        header: t("Scope 2 total (tCO₂e)"),
        render: (row) => row.totals?.scope2TotalTco2e || 0,
      },
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        render: (row) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("Actions")}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{t("Actions")}</DropdownMenuLabel>
                <Can
                  resource="carbon_surveys"
                  action="update"
                  userPermissions={permissions}
                >
                  <DropdownMenuItem
                    className="gap-2"
                    onSelect={() =>
                      navigate(`${PATHS.CARBON_SURVEYS}/${row.id}`)
                    }
                  >
                    <Settings className="h-4 w-4 text-blue-600" />
                    {t("Manage")}
                  </DropdownMenuItem>
                </Can>
                <DropdownMenuSeparator />
                <Can
                  resource="carbon_surveys"
                  action="delete"
                  userPermissions={permissions}
                >
                  <DropdownMenuItem
                    className="gap-2 text-destructive focus:text-destructive"
                    onSelect={() => onDeleteTarget(row)}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("Delete")}
                  </DropdownMenuItem>
                </Can>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [permissions, t],
  );

  useEffect(() => {
    getCarbonInventorySurvey();
  }, [page, pageSize]);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("Carbon Inventory Surveys")}
        description={t("View and manage your drafts and submissions.")}
        icon={ClipboardCheck}
        actions={
          <Can
            resource="carbon_surveys"
            action="create"
            userPermissions={permissions}
          >
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={() => navigate(PATHS.CARBON_SURVEYS_CREATE)}>
                <Plus className="mr-2 h-4 w-4" />
                {t("Create survey")}
              </Button>
            </div>
          </Can>
        }
      />
      <div className="rounded-radius border border-border bg-card p-4">
        <DataTable
          columns={columns}
          data={carbonSurveysList}
          emptyMessage={t("No target settings found.")}
          pagination={{
            page,
            pageSize,
            total,
            onPageChange: setPage,
            onPageSizeChange: setPageSize,
            pageSizeOptions: [10, 20, 50],
          }}
        />
        <DeleteCarbonSurveyDialog
          target={selectedSurvey}
          openDialog={deleteSurveyDialog}
          onClose={() => setDeleteSurveyDialog(null)}
        />
      </div>
    </div>
  );
};

export default observer(CarbonSurveysPage);
